import React, { useCallback, useContext, useEffectEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "src/store";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";

import {
    commandsApi,
    useCreateDeviceCommandMutation,
    useDeleteDeviceCommandMutation,
    useGetDeviceCommandsQuery,
} from "@dashboard/devices/api/commands";
import { selectDetailsLicense, selectPaneTab } from "@dashboard/devices/store/selectors";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { SortingState } from "@tanstack/react-table";

import { commandPollingInterval, emptyCommandList, getSortDirection, rowsPerPage } from "./commandProvider.constants";

function getSortCriteria(sorting: SortingState) {
    if (!sorting.length) {
        return "created";
    }
    const mapping: Record<string, string> = {
        date: "created",
        status: "status",
        origin: "origin",
        command: "caption",
    };
    return mapping[sorting[0].id] || "created";
}

type DeviceCommandContextType = {
    data: MdmCommand[];
    totalCount: number;
    isLoading: boolean;
    deletingId: number | null;
    resendingId: number | null;
    pageIndex: number;
    sorting: SortingState;
    pageSize: number;
    rowsPerPage: number[];
    onCreate: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    onDelete: (command: MdmCommand) => Promise<void>;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const DeviceCommandContext = React.createContext<DeviceCommandContextType>({
    data: [],
    totalCount: 0,
    isLoading: false,
    deletingId: null,
    resendingId: null,
    sorting: [{ id: "date", desc: true }],
    pageIndex: 0,
    pageSize: rowsPerPage[0],
    rowsPerPage,
    onCreate: () => Promise.resolve(),
    onDelete: () => Promise.resolve(),
    setSorting: () => {},
    setPageIndex: () => {},
    setPageSize: () => {},
});

const refreshableTabs = ["COMMANDS", "BWC"];

export const DeviceCommandProvider = (props: React.PropsWithChildren) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(rowsPerPage[0]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const license = useSelector(selectDetailsLicense);
    const selectedTab = useSelector(selectPaneTab);
    const [createCommand] = useCreateDeviceCommandMutation();
    const [deleteCommand] = useDeleteDeviceCommandMutation();

    const deviceId = license?.device?.id;
    const sortCriteria = getSortCriteria(sorting);
    const sortDirection = getSortDirection(sorting);

    const queryArg = useMemo(
        () => (deviceId ? { deviceId, pageIndex, pageSize, sortCriteria, sortDirection } : undefined),
        [deviceId, pageIndex, pageSize, sortCriteria, sortDirection],
    );

    const cachedPage = commandsApi.endpoints.getDeviceCommands.useQueryState(queryArg ?? skipToken).data;
    const shouldPoll = hasUnfinishedCommands(cachedPage?.list ?? emptyCommandList) && refreshableTabs.includes(selectedTab);

    const {
        data: commands,
        isFetching,
        isLoading: isInitialLoading,
        refetch,
    } = useGetDeviceCommandsQuery(queryArg ?? skipToken, {
        pollingInterval: shouldPoll ? commandPollingInterval : 0,
        skipPollingIfUnfocused: true,
        refetchOnFocus: shouldPoll,
        refetchOnMountOrArgChange: true,
    });

    const data = commands?.list ?? emptyCommandList;
    const totalCount = commands?.count ?? 0;
    const isLoading = useTableFetchLoading(isFetching, isInitialLoading, queryArg ?? null);

    const onCreate = useCallback(
        async (json: Record<string, unknown>, commandId: number | null) => {
            if (!deviceId || !queryArg) {
                return;
            }

            try {
                setResendingId(commandId);

                const newCommands = await createCommand({ deviceId, json }).unwrap();
                if (newCommands?.length) {
                    dispatch(
                        commandsApi.util.updateQueryData("getDeviceCommands", queryArg, (draft) => {
                            draft.list.unshift(...newCommands);
                            draft.count += newCommands.length;
                        }),
                    );
                    showSuccessToast(t("common:toast.commandEnqueued"));
                }
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandSentFailed"));
            } finally {
                setResendingId(null);
            }
        },
        [deviceId, queryArg, createCommand, dispatch, t],
    );

    const onDelete = useCallback(
        async (command: MdmCommand) => {
            if (!deviceId) {
                return;
            }

            try {
                setDeletingId(command.id);
                await deleteCommand({ deviceId, commandId: command.id }).unwrap();
                showSuccessToast(t("sidepanels:devicePanel.toast.commandDeletedSuccessfully"));
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandDeletedFailed"));
            } finally {
                setDeletingId(null);
            }
        },
        [deviceId, deleteCommand, t],
    );

    const previousTabRef = React.useRef(selectedTab);
    const refreshCommands = useEffectEvent(() => {
        void refetch();
    });

    React.useEffect(() => {
        const previousTab = previousTabRef.current;
        previousTabRef.current = selectedTab;

        if (previousTab !== selectedTab && refreshableTabs.includes(selectedTab)) {
            refreshCommands();
        }
    }, [selectedTab]);

    const contextValue = useMemo(
        () => ({
            data,
            totalCount,
            isLoading,
            deletingId,
            resendingId,
            sorting,
            pageIndex,
            pageSize,
            rowsPerPage,
            onCreate,
            onDelete,
            setSorting,
            setPageIndex,
            setPageSize,
        }),
        [data, totalCount, isLoading, deletingId, resendingId, sorting, pageIndex, pageSize, onCreate, onDelete],
    );

    return <DeviceCommandContext.Provider value={contextValue}>{props.children}</DeviceCommandContext.Provider>;
};

export const useDeviceCommand = () => {
    const context = useContext(DeviceCommandContext);
    if (!context) {
        throw new Error("useDeviceCommand must be used within a DeviceCommandProvider");
    }
    return context;
};
